import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";

import PossibleEvidences from "./PossibleEvidences";
import { copyToClipboard } from "@safecv/shared/stringUtils";
import { saveNeedsInputResponse } from "@safecv/actions/postActions";

import "./Inputs.css";

const selectStyles = {
  container: (styles) => ({
    ...styles,
    width: "100%",
    fontSize: "12px",
    fontWeight: "600",
    fontFamily: "Lato",
    fontStyle: "normal",
  }),
  control: (styles) => ({
    ...styles,
    minHeight: "25px",
    height: "25px",
    marginBottom: 0,
    boxShadow: "none",
    cursor: "pointer",
    borderRadius: "2px",
    border: "1px solid #e2e8f0",
    "&:hover": {
      border: "1px solid #4183c5",
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#676767",
    fontWeight: "normal",
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: "25px",
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: "25px",
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: "2",
  }),
};

const Inputs = (props) => {
  const {
    input,
    jobId,
    setSelectedSearchResult,
    handleUpdateNeedsInput,
    planStatus,
    annotationId,
  } = props;
  const [selectedAnswer, setSelectedAnswer] = useState({});
  const [isEditing, setIsEditing] = useState(true);
  const dispatch = useDispatch();

  const saveSelection = (value, id) => {
    let answers = { ...selectedAnswer };
    // if first question is being changed reset rest of the questions
    Object.keys(answers).forEach((currentId) => {
      if (currentId > id) answers[currentId] = null;
    });
    answers[id] = value;
    setSelectedAnswer(answers);
  };

  const sendResponse = () => {
    setIsEditing(false);
    dispatch(saveNeedsInputResponse(jobId, selectedAnswer, annotationId));
  };

  const returnQuestions = (type, id, value, showsearch, keywords) => {
    switch (type) {
      case "radio":
        return (
          <div className="input-radio">
            <input
              type="radio"
              name={id}
              value="yes"
              checked={selectedAnswer[id] === "yes"}
              className="input-radio-button"
              onChange={(e) => saveSelection(e.target.value, id)}
              disabled={planStatus === "Verified"}
            />
            <div htmlFor="yes" className="input-radio-text">
              Yes
            </div>
            <input
              type="radio"
              name={id}
              value="no"
              checked={selectedAnswer[id] === "no"}
              className="input-radio-button"
              onChange={(e) => saveSelection(e.target.value, id)}
              disabled={planStatus === "Verified"}
            />
            <div htmlFor="no" className="input-radio-text">
              No
            </div>
            {showsearch && (
              <PossibleEvidences
                keywords={keywords}
                jobId={jobId}
                setSelectedSearchResult={setSelectedSearchResult}
              />
            )}
          </div>
        );
      case "dropdown":
        const pageOptions = [...Array(Number(value))].map((_, index) => {
          return {
            value: index + 1,
            label: index + 1,
          };
        });
        let selectedValue = null;
        if (selectedAnswer[id]) {
          selectedValue = {
            value: selectedAnswer[id],
            label: selectedAnswer[id],
          };
        }
        return (
          <Select
            placeholder="Choose Page"
            options={pageOptions}
            styles={selectStyles}
            value={selectedValue}
            onChange={(e) => saveSelection(e.value, id)}
          />
        );
      case "textbox":
        if (!selectedAnswer[id]) saveSelection(value, id);
        return (
          <React.Fragment>
            <textarea
              rows="8"
              placeholder="Write here"
              className="input-textarea"
              value={selectedAnswer[id] || value}
              onChange={(e) => saveSelection(e.target.value, id)}
            />
          </React.Fragment>
        );
      case "end":
        return (
          <div className="input-done" onClick={() => sendResponse()}>
            Done
          </div>
        );
      case "doneCopy":
        return (
          <div
            className="input-done"
            onClick={() => {
              sendResponse();
              copyToClipboard(selectedAnswer[id]);
            }}
          >
            Done and Copy
          </div>
        );
      default:
        return null;
    }
  };

  // if response is saved, copy the same in the state
  useEffect(() => {
    let answers = { ...selectedAnswer };
    [...Array(Math.max(...input.map((a) => a.order)))].forEach((_, index) => {
      let prev = input.find((a) => a.order === index);
      let curr = input.find(
        (a) =>
          a.id ===
            (prev &&
              prev.response &&
              prev.options[prev.response].id &&
              Number(prev.options[prev.response].id)) ||
          (!prev && index + 1 === a.order)
      );
      if (curr && !selectedAnswer[curr.id]) {
        answers[curr.id] = curr.response;
      }
    });
    setSelectedAnswer(answers);
    if (input[0].response) setIsEditing(false);
  }, []);

  return (
    <React.Fragment>
      <div
        className={`input-container ${
          isEditing ? "" : "input-container-answered"
        }`}
      >
        <div className="input-header-container">
          {!isEditing && planStatus !== "Verified" && (
            <div className="input-edit" onClick={() => setIsEditing(true)}>
              Edit
            </div>
          )}
          <div className="input-close" onClick={() => handleUpdateNeedsInput()}>
            X
          </div>
        </div>

        {[...Array(Math.max(...input.map((a) => a.order)))].map((_, index) => {
          let prev = input.find((a) => a.order === index);
          let curr = input.find(
            (a) =>
              a.id ===
                (prev &&
                  selectedAnswer[prev.id] &&
                  prev.options[selectedAnswer[prev.id]].id &&
                  Number(prev.options[selectedAnswer[prev.id]].id)) ||
              (!prev && a.order === index + 1)
          );

          let isEnd =
            prev &&
            isEditing &&
            selectedAnswer[prev.id] &&
            prev.options[selectedAnswer[prev.id]].type === "end";

          let doneAnswers =
            curr && isEditing && selectedAnswer[curr.id] && !curr.options;

          return (
            <React.Fragment key={index}>
              {isEnd
                ? returnQuestions("end")
                : curr &&
                  (!prev || (prev && selectedAnswer[prev.id])) && (
                    <React.Fragment>
                      <div className="input-question"> {curr.question} </div>
                      {!isEditing ? (
                        <div className="input-response">
                          {selectedAnswer[curr.id]}
                        </div>
                      ) : (
                        returnQuestions(
                          curr.type,
                          curr.id,
                          curr.values,
                          curr.showSearch,
                          curr.keywords
                        )
                      )}
                      {doneAnswers &&
                        curr.type !== "textbox" &&
                        returnQuestions("end")}
                      {doneAnswers &&
                        curr.type === "textbox" &&
                        returnQuestions("doneCopy", curr.id)}
                    </React.Fragment>
                  )}
            </React.Fragment>
          );
        })}
      </div>
    </React.Fragment>
  );
};

export default Inputs;
